import { useEffect, useState } from "react";

/**
 * ScannerOverlay — wraps an image and renders a faux-scan HUD when `active` is true.
 * Scanline sweeps top→bottom, corner brackets, animated readouts (jaw 27%, skin 78%, etc).
 */
const READOUTS = [
  ["JAW", "scanning"],
  ["SKIN", "scanning"],
  ["EYES", "scanning"],
  ["SYMM", "scanning"],
  ["HARM", "scanning"],
  ["HAIR", "scanning"],
];

const FULL_EXTRA = [
  ["FRAME", "scanning"],
  ["BFAT", "scanning"],
  ["POST", "scanning"],
  ["HALO", "scanning"],
];

export default function ScannerOverlay({ active, mode = "face" }) {
  const items = mode === "full" ? [...READOUTS, ...FULL_EXTRA] : READOUTS;
  const [pct, setPct] = useState(0);
  const [coords, setCoords] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!active) {
      setPct(0);
      return;
    }
    let cancel = false;
    const tick = () => {
      if (cancel) return;
      setPct((p) => (p >= 99 ? 12 : p + 1));
      setCoords({ x: Math.random() * 100, y: Math.random() * 100 });
      setTimeout(tick, 280);
    };
    tick();
    return () => {
      cancel = true;
    };
  }, [active]);

  if (!active) return null;

  return (
    <div className="absolute inset-0 pointer-events-none z-10" data-testid="scanner-overlay">
      {/* matrix grid */}
      <div className="absolute inset-0 matrix-grid opacity-50" />
      {/* corner brackets */}
      <div className="absolute inset-0 brackets">
        <span className="b-tr" />
        <span className="b-bl" />
      </div>
      {/* scanline */}
      <div className="scanline" />

      {/* Top HUD */}
      <div className="absolute top-3 left-3 right-3 flex justify-between text-[10px] font-mono uppercase tracking-widest text-[var(--primary)]">
        <span className="bg-black/70 px-2 py-1 flicker">● ASCEND-VISION v3</span>
        <span className="bg-black/70 px-2 py-1">
          x:{coords.x.toFixed(2)} y:{coords.y.toFixed(2)}
        </span>
      </div>

      {/* Right column metrics */}
      <div className="absolute top-14 right-3 w-40 space-y-1">
        {items.map(([k], i) => (
          <div
            key={k}
            className="flex justify-between items-center bg-black/55 px-2 py-1 border border-[var(--primary-dim)] text-[9px] font-mono uppercase tracking-widest"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <span className="text-[var(--text-muted)]">{k}</span>
            <span className="text-[var(--primary)] glow blink">SCAN…</span>
          </div>
        ))}
      </div>

      {/* Bottom progress */}
      <div className="absolute bottom-3 left-3 right-3">
        <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-[var(--primary)] mb-1.5">
          <span>ANALYZING SUBJECT</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1 bg-black/70 border border-[var(--primary-dim)] relative overflow-hidden">
          <div
            className="h-full bg-[var(--primary)] transition-all"
            style={{ width: `${pct}%`, boxShadow: "0 0 14px var(--primary)" }}
          />
        </div>
      </div>
    </div>
  );
}
